import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonCard,
  IonCardContent,
  IonList,
  IonItem,
  IonLabel,
  IonNote,
  IonIcon,
  IonBadge,
  IonBackButton,
  IonButtons,
  IonToast,
  IonSelect,
  IonSelectOption,
  IonAlert,
} from '@ionic/react';
import { add, create, close } from 'ionicons/icons';
import { useMyEmployerProfile, useEmployerJobs, useCloseJobListing } from '@/lib/api/hooks';
import { NoindexSeo } from '@/components/NoindexSeo';
import { AppFooter } from '@/components/AppFooter';

export function JobListings() {
  const history = useHistory();
  const { data: employerProfile } = useMyEmployerProfile();
  const employerId = employerProfile?._id ?? '';
  const tenantId = employerProfile?.tenant?.id ?? '';
  const { data: jobsEnvelope, isLoading } = useEmployerJobs(employerId, tenantId);
  const closeJob = useCloseJobListing();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [toast, setToast] = useState<{ msg: string; color: string } | null>(null);
  const [closingJobId, setClosingJobId] = useState<string | null>(null);

  const jobs = jobsEnvelope?.data ?? [];
  const filtered = jobs.filter((j) => statusFilter === 'all' || j.status === statusFilter);

  const statusColor = (s: string) => {
    switch (s) {
      case 'Open': return 'success';
      case 'Closed': return 'medium';
      case 'Filled': return 'primary';
      case 'Expired': return 'danger';
      default: return 'medium';
    }
  };

  const handleClose = (id: string) => {
    setClosingJobId(id);
  };

  const confirmClose = () => {
    if (!closingJobId) return;
    closeJob.mutate(closingJobId, {
      onSuccess: () => {
        setToast({ msg: 'Job closed', color: 'success' });
        setClosingJobId(null);
      },
      onError: (err: Error) => {
        setToast({ msg: err.message || 'Failed to close job', color: 'danger' });
        setClosingJobId(null);
      },
    });
  };

  return (
    <IonPage>
      <NoindexSeo />
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonBackButton defaultHref="/employer" /></IonButtons>
          <IonTitle>Job Listings</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => history.push('/employer/jobs/new')}><IonIcon icon={add} /></IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <IonItem>
          <IonSelect label="Filter by status" value={statusFilter} onIonChange={(e) => setStatusFilter(e.detail.value)}>
            <IonSelectOption value="all">All</IonSelectOption>
            <IonSelectOption value="Open">Open</IonSelectOption>
            <IonSelectOption value="Closed">Closed</IonSelectOption>
            <IonSelectOption value="Filled">Filled</IonSelectOption>
            <IonSelectOption value="Expired">Expired</IonSelectOption>
          </IonSelect>
        </IonItem>

        {isLoading ? (
          <p className="bp-subheading">Loading…</p>
        ) : filtered.length === 0 ? (
          <IonCard>
            <IonCardContent className="ion-text-center">
              <p className="bp-subheading">No job listings yet.</p>
              <IonButton onClick={() => history.push('/employer/jobs/new')}>
                <IonIcon icon={add} slot="start" /> Post your first job
              </IonButton>
            </IonCardContent>
          </IonCard>
        ) : (
          <IonList lines="full">
            {filtered.map((j) => (
              <IonItem key={j._id}>
                <IonLabel>
                  <h3>{j.title}</h3>
                  <IonNote>{j.location} · {j.workType} · Posted {new Date(j.metadata.createdAt).toLocaleDateString()}</IonNote>
                </IonLabel>
                <IonBadge color={statusColor(j.status)} slot="end">{j.status}</IonBadge>
                <IonButton fill="clear" slot="end" onClick={() => history.push(`/employer/jobs/${j._id}/edit`)}>
                  <IonIcon icon={create} />
                </IonButton>
                {j.status === 'Open' && (
                  <IonButton fill="clear" slot="end" color="danger" onClick={() => handleClose(j._id)}>
                    <IonIcon icon={close} />
                  </IonButton>
                )}
              </IonItem>
            ))}
          </IonList>
        )}
        <AppFooter />

      </IonContent>
      <IonToast isOpen={!!toast} message={toast?.msg} color={toast?.color} duration={2000} onDidDismiss={() => setToast(null)} />
      <IonAlert
        isOpen={!!closingJobId}
        header="Close job listing?"
        message="This job will no longer appear in search results. Candidates cannot apply to closed jobs."
        buttons={[
          { text: 'Cancel', role: 'cancel', handler: () => setClosingJobId(null) },
          { text: 'Close', role: 'confirm', handler: confirmClose },
        ]}
      />
    </IonPage>
  );
}
